import { Button, Calendar, Clock, Input, Modal } from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AvailabilityFormData {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
  isRecurring: boolean;
  slotDurationMinutes?: number;
}

interface AvailabilityFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AvailabilityFormData) => Promise<void>;
  isSubmitting?: boolean;
  initialData?: Partial<AvailabilityFormData>;
}

const DAYS_OF_WEEK = [
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
  { value: "SUNDAY", label: "Sunday" },
];

export const AvailabilityForm: React.FC<AvailabilityFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<AvailabilityFormData>({
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    daysOfWeek: [],
    isRecurring: false,
    slotDurationMinutes: 30,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        startDate: initialData.startDate || "",
        endDate: initialData.endDate || "",
        startTime: initialData.startTime || "",
        endTime: initialData.endTime || "",
        daysOfWeek: initialData.daysOfWeek || [],
        isRecurring: initialData.isRecurring || false,
        slotDurationMinutes: initialData.slotDurationMinutes || 30,
      });
    } else {
      // Reset form when opening for new availability
      setFormData({
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        daysOfWeek: [],
        isRecurring: false,
        slotDurationMinutes: 30,
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleDayToggle = (day: string) => {
    setFormData((prev) => {
      const days = prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day];
      return { ...prev, daysOfWeek: days };
    });
    if (errors.daysOfWeek) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.daysOfWeek;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate) {
      newErrors.startDate = t("availability.errors.startDateRequired");
    }
    if (!formData.endDate) {
      newErrors.endDate = t("availability.errors.endDateRequired");
    }
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start > end) {
        newErrors.endDate = t("availability.errors.endDateBeforeStart");
      }
    }
    if (!formData.startTime) {
      newErrors.startTime = t("availability.errors.startTimeRequired");
    }
    if (!formData.endTime) {
      newErrors.endTime = t("availability.errors.endTimeRequired");
    }
    if (formData.startTime && formData.endTime) {
      const [startHours, startMinutes] = formData.startTime
        .split(":")
        .map(Number);
      const [endHours, endMinutes] = formData.endTime.split(":").map(Number);
      const startTotal = startHours * 60 + startMinutes;
      const endTotal = endHours * 60 + endMinutes;
      if (startTotal >= endTotal) {
        newErrors.endTime = t("availability.errors.endTimeBeforeStart");
      }
    }
    if (formData.daysOfWeek.length === 0) {
      newErrors.daysOfWeek = t("availability.errors.daysRequired");
    }
    if (
      formData.slotDurationMinutes &&
      (formData.slotDurationMinutes < 15 || formData.slotDurationMinutes > 240)
    ) {
      newErrors.slotDurationMinutes = t(
        "availability.errors.slotDurationInvalid"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error("Error submitting availability:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        initialData
          ? t("availability.editAvailability", "Edit Availability")
          : t("availability.addAvailability", "Add Availability")
      }
      variant="info"
      size="2xl"
      closeOnOverlayClick={!isSubmitting}
      contentClassName="max-h-[75vh] overflow-y-auto"
    >
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Date Range */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Input
                label={t("availability.startDate", "Start Date")}
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                error={errors.startDate}
                disabled={isSubmitting}
                leftIcon={<Calendar className="w-4 h-4" />}
              />
            </div>
            <div>
              <Input
                label={t("availability.endDate", "End Date")}
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                required
                error={errors.endDate}
                disabled={isSubmitting}
                leftIcon={<Calendar className="w-4 h-4" />}
              />
            </div>
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Input
                label={t("availability.startTime", "Start Time")}
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                error={errors.startTime}
                disabled={isSubmitting}
                leftIcon={<Clock className="w-4 h-4" />}
              />
            </div>
            <div>
              <Input
                label={t("availability.endTime", "End Time")}
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                error={errors.endTime}
                disabled={isSubmitting}
                leftIcon={<Clock className="w-4 h-4" />}
              />
            </div>
          </div>

          {/* Slot Duration */}
          <div>
            <Input
              label={t("availability.slotDuration", "Slot Duration (minutes)")}
              type="number"
              name="slotDurationMinutes"
              value={formData.slotDurationMinutes?.toString() || ""}
              onChange={handleInputChange}
              min={15}
              max={240}
              step={15}
              error={errors.slotDurationMinutes}
              disabled={isSubmitting}
              leftIcon={<Clock className="w-4 h-4" />}
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t(
                "availability.slotDurationHint",
                "Duration of each appointment slot (15-240 minutes)"
              )}
            </p>
          </div>

          {/* Days of Week */}
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-700">
              {t("availability.daysOfWeek", "Days of Week")} *
            </label>
            {errors.daysOfWeek && (
              <p className="mb-2 text-sm text-danger-600">
                {errors.daysOfWeek}
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {DAYS_OF_WEEK.map((day) => (
                <label
                  key={day.value}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${formData.daysOfWeek.includes(day.value)
                      ? "bg-primary-50 border-primary-300 text-primary-700"
                      : "bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.daysOfWeek.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                    disabled={isSubmitting}
                    className="mr-2 w-4 h-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium">{day.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Recurring */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isRecurring"
              checked={formData.isRecurring}
              onChange={handleInputChange}
              disabled={isSubmitting}
              className="mr-2 w-4 h-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
              id="isRecurring"
            />
            <label
              htmlFor="isRecurring"
              className="text-sm font-medium cursor-pointer text-neutral-700"
            >
              {t("availability.isRecurring", "Recurring availability")}
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-4 space-x-3 border-t border-neutral-200">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" variant="ews-primary" disabled={isSubmitting}>
              {isSubmitting
                ? t("common.saving", "Saving...")
                : initialData
                  ? t("common.update", "Update")
                  : t("common.create", "Create")}
            </Button>
          </div>
        </form>
    </Modal>
  );
};
